import React, { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { kr, dato } from '../lib/format';
import { PageHeader, Card, Modal, Tabs } from '../components/ui';
import {
  Plus, Pencil, Trash2, Inbox, CheckCircle2, XCircle,
  CreditCard, Paperclip, AlertTriangle, FileText, Download,
} from 'lucide-react';

const tomLF = () => ({
  leverandor_id: '', leverandor_navn: '', leverandor_orgnr: '',
  fakturanummer: '', kid: '', kontonr: '',
  dato: new Date().toISOString().slice(0, 10),
  forfall: '',
  beskrivelse: '', kategori: '',
  sum_eks_mva: '', mva_sats: 25, mva_belop: '', sum_ink_mva: '',
  prosjekt_id: '', notater: '', lagre_kontakt: false,
});

const STATUS_BADGE = {
  mottatt:  { tekst: 'Mottatt',  farge: '#3b82f6' },
  godkjent: { tekst: 'Godkjent', farge: '#0ea5e9' },
  betalt:   { tekst: 'Betalt',   farge: '#16a34a' },
  avvist:   { tekst: 'Avvist',   farge: '#dc2626' },
};

const Leverandorfaktura = () => {
  const [liste, setListe] = useState(null);
  const [statusFilter, setStatusFilter] = useState('alle');
  const [leverandorer, setLeverandorer] = useState([]);
  const [prosjekter, setProsjekter] = useState([]);
  const [open, setOpen] = useState(false);
  const [redigerer, setRedigerer] = useState(null);
  const [skjema, setSkjema] = useState(tomLF());
  const [betalOpen, setBetalOpen] = useState(null);
  const [betalDato, setBetalDato] = useState(new Date().toISOString().slice(0, 10));

  const last = useCallback(() => {
    api.get('/lev-faktura', { params: { status: statusFilter } })
       .then((r) => setListe(r.data));
  }, [statusFilter]);

  useEffect(() => { last(); }, [last]);
  useEffect(() => {
    api.get('/kontakter').then((r) => setLeverandorer(
      r.data.filter((k) => (k.type || '').toLowerCase().startsWith('lever')),
    )).catch(() => {});
    api.get('/prosjekt').then((r) => setProsjekter(r.data)).catch(() => {});
  }, []);

  const oppdaterSum = (s) => {
    const eks = parseFloat(s.sum_eks_mva) || 0;
    const sats = parseFloat(s.mva_sats) || 0;
    const mva = +(eks * sats / 100).toFixed(2);
    const ink = +(eks + mva).toFixed(2);
    return { ...s, mva_belop: mva, sum_ink_mva: ink };
  };

  const sett = (felt, verdi) => {
    setSkjema((s) => {
      const ny = { ...s, [felt]: verdi };
      if (['sum_eks_mva', 'mva_sats'].includes(felt)) return oppdaterSum(ny);
      if (felt === 'leverandor_id' && verdi) {
        const lev = leverandorer.find((k) => String(k.id) === String(verdi));
        if (lev) {
          ny.leverandor_navn = lev.navn || ny.leverandor_navn;
          ny.leverandor_orgnr = lev.orgnr || ny.leverandor_orgnr;
          ny.kontonr = lev.kontonr || ny.kontonr;
        }
      }
      return ny;
    });
  };

  const aapneNy = () => {
    setRedigerer(null);
    setSkjema(tomLF());
    setOpen(true);
  };

  const aapneRediger = (lf) => {
    setRedigerer(lf);
    setSkjema({
      leverandor_id: lf.leverandor_id || '',
      leverandor_navn: lf.leverandor_navn,
      leverandor_orgnr: lf.leverandor_orgnr || '',
      fakturanummer: lf.fakturanummer || '',
      kid: lf.kid || '',
      kontonr: lf.kontonr || '',
      dato: lf.dato, forfall: lf.forfall,
      beskrivelse: lf.beskrivelse || '',
      kategori: lf.kategori || '',
      sum_eks_mva: lf.sum_eks_mva || '',
      mva_sats: lf.mva_sats || 25,
      mva_belop: lf.mva_belop || '',
      sum_ink_mva: lf.sum_ink_mva || '',
      prosjekt_id: lf.prosjekt_id || '',
      notater: lf.notater || '',
      lagre_kontakt: false,
    });
    setOpen(true);
  };

  const lagre = async () => {
    if (!skjema.leverandor_navn || !skjema.dato || !skjema.forfall) {
      alert('Leverandør, dato og forfall er påkrevd.');
      return;
    }
    const payload = {
      ...skjema,
      leverandor_id: skjema.leverandor_id || null,
      prosjekt_id: skjema.prosjekt_id || null,
      sum_eks_mva: parseFloat(skjema.sum_eks_mva) || 0,
      mva_sats: parseFloat(skjema.mva_sats) || 0,
      mva_belop: parseFloat(skjema.mva_belop) || 0,
      sum_ink_mva: parseFloat(skjema.sum_ink_mva) || 0,
    };
    try {
      if (redigerer) await api.put(`/lev-faktura/${redigerer.id}`, payload);
      else await api.post('/lev-faktura', payload);
      setOpen(false); last();
    } catch (e) {
      alert(e.response?.data?.error || 'Klarte ikke lagre.');
    }
  };

  const slett = async (lf) => {
    if (!window.confirm(`Slette leverandørfaktura fra «${lf.leverandor_navn}»?`)) return;
    try { await api.delete(`/lev-faktura/${lf.id}`); last(); }
    catch (e) { alert(e.response?.data?.error || 'Klarte ikke slette.'); }
  };

  const godkjenn = async (lf) => {
    try { await api.post(`/lev-faktura/${lf.id}/godkjenn`); last(); }
    catch (e) { alert(e.response?.data?.error || 'Feilet.'); }
  };

  const avvis = async (lf) => {
    if (!window.confirm(`Avvise faktura fra «${lf.leverandor_navn}»?`)) return;
    try { await api.post(`/lev-faktura/${lf.id}/avvis`); last(); }
    catch (e) { alert(e.response?.data?.error || 'Feilet.'); }
  };

  const aapneBetal = (lf) => {
    setBetalOpen(lf);
    setBetalDato(new Date().toISOString().slice(0, 10));
  };

  const bekreftBetal = async () => {
    try {
      await api.post(`/lev-faktura/${betalOpen.id}/betal`, { dato: betalDato });
      setBetalOpen(null); last();
    } catch (e) { alert(e.response?.data?.error || 'Feilet.'); }
  };

  const eksporterPain001 = async () => {
    const godkjente = (liste || []).filter((lf) => lf.status === 'godkjent');
    if (!godkjente.length) {
      alert('Ingen godkjente leverandorfakturaer aa eksportere. Godkjenn fakturaer forst.');
      return;
    }
    if (!window.confirm(
      `Eksporter ${godkjente.length} godkjent(e) faktura(er) (totalt ${kr(
        godkjente.reduce((s, lf) => s + (lf.sum_ink_mva || 0), 0)
      )}) til nettbank-fil?`
    )) return;
    try {
      const r = await api.post(
        '/lev-faktura/eksport-pain001',
        { ids: godkjente.map((lf) => lf.id) },
        { responseType: 'blob' }
      );
      const blob = new Blob([r.data], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `betalinger_${new Date().toISOString().slice(0, 10)}.xml`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      const txt = e.response?.data instanceof Blob
        ? await e.response.data.text()
        : (e.response?.data?.error || 'Eksport feilet.');
      try { alert(JSON.parse(txt).error || txt); } catch { alert(txt); }
    }
  };

  if (!liste) return <span className="fm-text-muted">Laster…</span>;

  const utestaaende = liste
    .filter((lf) => lf.status === 'mottatt' || lf.status === 'godkjent')
    .reduce((sum, lf) => sum + (lf.sum_ink_mva || 0), 0);
  const forfaltAntall = liste.filter((lf) => lf.forfalt).length;

  return (
    <>
      <PageHeader
        title="Innkjøpsfakturaer"
        subtitle="Registrer fakturaer fra leverandører og bokfør ved betaling"
        actions={<>
          <button className="btn-ghost" onClick={eksporterPain001} title="Last ned ISO 20022 pain.001-fil for opplasting i nettbank">
            <Download size={14} /> Eksporter til nettbank
          </button>
          <button className="btn-primary" onClick={aapneNy}><Plus size={14} /> Ny innkjøpsfaktura</button>
        </>}
      />

      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 12 }}>
        <Card style={{ padding: 14 }}>
          <div className="fm-text-muted" style={{ fontSize: 11, textTransform: 'uppercase' }}>Utestående</div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{kr(utestaaende)}</div>
        </Card>
        <Card style={{ padding: 14 }}>
          <div className="fm-text-muted" style={{ fontSize: 11, textTransform: 'uppercase' }}>Forfalt</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: forfaltAntall ? 'var(--theme-danger)' : undefined }}>
            {forfaltAntall}
          </div>
        </Card>
      </div>

      <Tabs
        value={statusFilter}
        onChange={setStatusFilter}
        items={['alle', 'mottatt', 'godkjent', 'betalt', 'avvist'].map((s) => ({
          key: s,
          label: s === 'alle' ? 'Alle' : (STATUS_BADGE[s]?.tekst || s),
        }))}
      />

      <Card style={{ padding: 0 }}>
        {liste.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <Inbox size={32} className="fm-text-muted" style={{ marginBottom: 8 }} />
            <p className="fm-text-muted" style={{ margin: 0, fontSize: 13 }}>Ingen innkjøpsfakturaer.</p>
          </div>
        ) : (
          <table className="fm-table">
            <thead>
              <tr>
                <th>Leverandør</th>
                <th>Faktura&nbsp;nr</th>
                <th>Dato</th>
                <th>Forfall</th>
                <th>Beløp</th>
                <th>Status</th>
                <th>Prosjekt</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {liste.map((lf) => {
                const sb = STATUS_BADGE[lf.status] || { tekst: lf.status, farge: '#666' };
                return (
                  <tr key={lf.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{lf.leverandor_navn}</div>
                      {lf.beskrivelse && (
                        <div className="fm-text-muted" style={{ fontSize: 11 }}>{lf.beskrivelse}</div>
                      )}
                    </td>
                    <td>{lf.fakturanummer || '—'}</td>
                    <td>{dato(lf.dato)}</td>
                    <td>
                      {dato(lf.forfall)}
                      {lf.forfalt && (
                        <span style={{ marginLeft: 4, color: 'var(--theme-danger)' }}>
                          <AlertTriangle size={12} />
                        </span>
                      )}
                    </td>
                    <td style={{ fontWeight: 600 }}>{kr(lf.sum_ink_mva)}</td>
                    <td>
                      <span style={{
                        background: sb.farge, color: '#fff', padding: '2px 8px',
                        borderRadius: 10, fontSize: 11, fontWeight: 600,
                      }}>{sb.tekst}</span>
                      {lf.bilag_id && (
                        <span className="fm-text-muted" style={{ marginLeft: 6, fontSize: 11 }}>
                          <FileText size={10} /> bilag #{lf.bilag_id}
                        </span>
                      )}
                    </td>
                    <td>{lf.prosjekt_navn || '—'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {lf.vedlegg?.length > 0 && (
                        <span className="fm-text-muted" style={{ fontSize: 11, marginRight: 6 }}>
                          <Paperclip size={10} /> {lf.vedlegg.length}
                        </span>
                      )}
                      {lf.status === 'mottatt' && (
                        <button className="btn-ghost" title="Godkjenn" onClick={() => godkjenn(lf)}>
                          <CheckCircle2 size={14} style={{ color: 'var(--theme-success)' }} />
                        </button>
                      )}
                      {(lf.status === 'godkjent' || lf.status === 'mottatt') && (
                        <button className="btn-ghost" title="Bokfør som betalt" onClick={() => aapneBetal(lf)}>
                          <CreditCard size={14} style={{ color: 'var(--theme-primary)' }} />
                        </button>
                      )}
                      {lf.status !== 'betalt' && lf.status !== 'avvist' && (
                        <button className="btn-ghost" title="Avvis" onClick={() => avvis(lf)}>
                          <XCircle size={14} style={{ color: 'var(--theme-warning)' }} />
                        </button>
                      )}
                      {lf.status !== 'betalt' && (
                        <button className="btn-ghost" title="Rediger" onClick={() => aapneRediger(lf)}>
                          <Pencil size={14} />
                        </button>
                      )}
                      {!lf.bilag_id && (
                        <button className="btn-ghost" title="Slett" onClick={() => slett(lf)}>
                          <Trash2 size={14} style={{ color: 'var(--theme-danger)' }} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      <Modal open={open} onClose={() => setOpen(false)}
             title={redigerer ? 'Rediger innkjøpsfaktura' : 'Ny innkjøpsfaktura'}
             width={720}
             footer={<>
               <button className="btn-ghost" onClick={() => setOpen(false)}>Avbryt</button>
               <button className="btn-primary" onClick={lagre}>Lagre</button>
             </>}>
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label>Leverandør</label>
            <select value={skjema.leverandor_id} onChange={(e) => sett('leverandor_id', e.target.value)}>
              <option value="">— ny leverandør (skriv inn under) —</option>
              {leverandorer.map((k) => (
                <option key={k.id} value={k.id}>{k.navn}</option>
              ))}
            </select>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label>Leverandørnavn *</label>
            <input value={skjema.leverandor_navn}
                   onChange={(e) => sett('leverandor_navn', e.target.value)} />
          </div>
          <div>
            <label>Org.nr</label>
            <input value={skjema.leverandor_orgnr}
                   onChange={(e) => sett('leverandor_orgnr', e.target.value)} />
          </div>
          <div>
            <label>Bankkonto (deres)</label>
            <input value={skjema.kontonr} onChange={(e) => sett('kontonr', e.target.value)} />
          </div>
          {!skjema.leverandor_id && skjema.leverandor_navn && (
            <label className="flex items-center" style={{ gridColumn: '1 / -1', gap: 6, fontSize: 12 }}>
              <input type="checkbox" checked={!!skjema.lagre_kontakt}
                     onChange={(e) => sett('lagre_kontakt', e.target.checked)} />
              Lagre som ny kontakt (leverandør)
            </label>
          )}
          <div>
            <label>Fakturanr (deres)</label>
            <input value={skjema.fakturanummer}
                   onChange={(e) => sett('fakturanummer', e.target.value)} />
          </div>
          <div>
            <label>KID</label>
            <input value={skjema.kid} onChange={(e) => sett('kid', e.target.value)} />
          </div>
          <div>
            <label>Fakturadato *</label>
            <input type="date" value={skjema.dato} onChange={(e) => sett('dato', e.target.value)} />
          </div>
          <div>
            <label>Forfallsdato *</label>
            <input type="date" value={skjema.forfall} onChange={(e) => sett('forfall', e.target.value)} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label>Beskrivelse</label>
            <input value={skjema.beskrivelse} onChange={(e) => sett('beskrivelse', e.target.value)} />
          </div>
          <div>
            <label>Kategori (utgift)</label>
            <input value={skjema.kategori} onChange={(e) => sett('kategori', e.target.value)}
                   placeholder="f.eks. Kontorrekvisita" />
          </div>
          <div>
            <label>Prosjekt</label>
            <select value={skjema.prosjekt_id}
                    onChange={(e) => sett('prosjekt_id', e.target.value)}>
              <option value="">— ingen —</option>
              {prosjekter.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.kode ? `${p.kode} · ` : ''}{p.navn}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>Beløp eks. mva</label>
            <input type="number" step="0.01" value={skjema.sum_eks_mva}
                   onChange={(e) => sett('sum_eks_mva', e.target.value)} />
          </div>
          <div>
            <label>MVA-sats (%)</label>
            <input type="number" step="0.01" value={skjema.mva_sats}
                   onChange={(e) => sett('mva_sats', e.target.value)} />
          </div>
          <div>
            <label>MVA-beløp</label>
            <input type="number" step="0.01" value={skjema.mva_belop}
                   onChange={(e) => sett('mva_belop', e.target.value)} />
          </div>
          <div>
            <label>Sum ink. mva</label>
            <input type="number" step="0.01" value={skjema.sum_ink_mva}
                   onChange={(e) => sett('sum_ink_mva', e.target.value)} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label>Notater</label>
            <textarea rows={2} value={skjema.notater}
                      onChange={(e) => sett('notater', e.target.value)} />
          </div>
        </div>
        {redigerer && <Vedleggsliste lf={redigerer} onChange={last} />}
      </Modal>

      <Modal open={!!betalOpen} onClose={() => setBetalOpen(null)}
             title="Bokfør som betalt"
             footer={<>
               <button className="btn-ghost" onClick={() => setBetalOpen(null)}>Avbryt</button>
               <button className="btn-primary" onClick={bekreftBetal}>Bokfør</button>
             </>}>
        {betalOpen && (
          <div>
            <p style={{ marginTop: 0 }}>
              Faktura fra <b>{betalOpen.leverandor_navn}</b> på <b>{kr(betalOpen.sum_ink_mva)}</b> blir
              bokført som utgiftsbilag og markert betalt.
            </p>
            <label>Betalingsdato</label>
            <input type="date" value={betalDato} onChange={(e) => setBetalDato(e.target.value)} />
          </div>
        )}
      </Modal>
    </>
  );
};

const Vedleggsliste = ({ lf, onChange }) => {
  const [filer, setFiler] = useState(lf.vedlegg || []);
  const [busy, setBusy] = useState(false);

  const lastOpp = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('fil', f);
      const r = await api.post(`/lev-faktura/${lf.id}/vedlegg`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFiler([...filer, r.data]);
      onChange?.();
    } catch (err) {
      alert(err.response?.data?.error || 'Opplasting feilet.');
    }
    setBusy(false);
    e.target.value = '';
  };

  const slett = async (v) => {
    if (!window.confirm(`Slett ${v.filnavn}?`)) return;
    await api.delete(`/lev-faktura-vedlegg/${v.id}`);
    setFiler(filer.filter((x) => x.id !== v.id));
    onChange?.();
  };

  return (
    <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--theme-border)' }}>
      <h4 style={{ fontSize: 12, fontWeight: 600, margin: '0 0 8px' }}>
        <Paperclip size={12} /> Vedlegg ({filer.length})
      </h4>
      {filer.map((v) => (
        <div key={v.id} className="flex items-center justify-between"
             style={{ padding: '6px 8px', marginBottom: 4, background: 'var(--theme-bg-input)', borderRadius: 4, fontSize: 12 }}>
          <a href={`/api/lev-faktura-vedlegg/${v.id}`} target="_blank" rel="noreferrer" className="fm-text-primary">
            {v.filnavn} <span className="fm-text-muted">({Math.round(v.storrelse / 1024)} kB)</span>
          </a>
          <button className="btn-ghost" onClick={() => slett(v)} style={{ color: 'var(--theme-danger)' }}>
            <Trash2 size={12} />
          </button>
        </div>
      ))}
      <label className="btn-ghost" style={{ cursor: 'pointer', display: 'inline-flex', marginTop: 6 }}>
        <Paperclip size={12} /> {busy ? 'Laster opp…' : 'Last opp fil'}
        <input type="file" hidden onChange={lastOpp} disabled={busy} />
      </label>
    </div>
  );
};

export default Leverandorfaktura;
